import React, { useState, useEffect } from "react";
import styles from "../../styles/pwainstallprompt.module.scss";
import { logger } from "../../utils/logger";

interface PWAInstallPromptProps {
  // Optional: callback when user installs
  onInstall?: () => void;
  // Optional: callback when user dismisses
  onDismiss?: () => void;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({
  onInstall,
  onDismiss,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (running in standalone mode)
    const checkStandalone = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true;
      setIsStandalone(standalone);
      return standalone;
    };

    // Check if user has dismissed the prompt before
    const checkDismissed = () => {
      // "Later" button uses sessionStorage (this session only)
      const dismissedThisSession = sessionStorage.getItem(
        "pwa-install-dismissed",
      );
      // "Don't show again" button uses localStorage (permanent)
      const dontShowAgain = localStorage.getItem("pwa-install-dont-show");
      return dismissedThisSession === "true" || dontShowAgain === "true";
    };

    // Check if prompt should be shown
    const checkPromptAvailability = () => {
      if (checkStandalone()) {
        logger.pwa.log("App already installed, not showing install prompt");
        return;
      }

      if (checkDismissed()) {
        logger.pwa.log("Install prompt previously dismissed");
        return;
      }

      if (window.deferredPrompt) {
        logger.pwa.log("Install prompt available, showing banner");
        setShowPrompt(true);
      }
    };

    // Initial check
    checkPromptAvailability();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = () => {
      logger.pwa.log("Install prompt event detected");
      setTimeout(checkPromptAvailability, 100); // Small delay to ensure window.deferredPrompt is set
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      logger.pwa.log("App installed, hiding install prompt");
      setShowPrompt(false);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check periodically in case deferredPrompt becomes available after mount
    const intervalId = setInterval(() => {
      if (
        !showPrompt &&
        window.deferredPrompt &&
        !checkStandalone() &&
        !checkDismissed()
      ) {
        logger.pwa.log("Deferred prompt now available, showing banner");
        setShowPrompt(true);
        clearInterval(intervalId);
      }
    }, 1000);

    // Cleanup after 10 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 10000);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [showPrompt]);

  const handleInstall = async () => {
    if (!window.deferredPrompt) {
      logger.pwa.warn("No install prompt available");
      return;
    }

    try {
      logger.pwa.log("Showing install prompt");
      await window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      logger.pwa.log(`User response to install prompt: ${outcome}`);

      if (outcome === "accepted") {
        logger.pwa.log("User accepted the install prompt");
        setShowPrompt(false);
        onInstall?.();
      } else {
        logger.pwa.log("User dismissed the install prompt");
        handleDismiss();
      }

      window.deferredPrompt = null;
    } catch (error) {
      logger.pwa.error("Error showing install prompt:", error);
    }
  };

  const handleDismiss = () => {
    logger.pwa.log("Install banner dismissed for this session");
    setShowPrompt(false);
    // Use sessionStorage so it reappears on next visit
    sessionStorage.setItem("pwa-install-dismissed", "true");
    onDismiss?.();
  };

  const handleDontShowAgain = () => {
    logger.pwa.log("Install banner dismissed permanently");
    setShowPrompt(false);
    localStorage.setItem("pwa-install-dont-show", "true");
    onDismiss?.();
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className={styles.promptContainer}>
      <div className={styles.promptContent}>
        <div className={styles.promptIcon}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect width="40" height="40" rx="8" fill="#0C294A" />
            <path
              d="M20 12V28M20 28L14 22M20 28L26 22"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className={styles.promptText}>
          <h3 className={styles.promptTitle}>Install SellSmart</h3>
          <p className={styles.promptDescription}>
            Install our app for faster access and offline support
          </p>
        </div>
        <div className={styles.promptActions}>
          <button
            className={styles.installButton}
            onClick={handleInstall}
            aria-label="Install app"
          >
            Install
          </button>
          <button
            className={styles.dismissButton}
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            Later
          </button>
          <button
            className={styles.closeButton}
            onClick={handleDontShowAgain}
            aria-label="Don't show again"
            title="Don't show again"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
